import React, { useState, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { apiGetCartDetails } from 'api/cart';
import { apiGetOrderTypeDetail } from 'api/orderType';
import { apiGetClientOrderDetail } from 'api/order';
import { apiGetPackages } from 'api/packages';
import isArray from 'lodash/isArray';
import findIndex from 'lodash/findIndex';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import Tabs from 'components/Tabs';
import Button, { ButtonColors } from 'components/Button';
import { getUrlParamValueByKey, removeAllSearchParam } from 'utils/routes';
import PageContent from 'components/PageContent';
import RouterLeavingGuard from 'components/RouterLeavingGuard';
import { getErrorMessageFromObj } from 'utils/object';
import { useIsInternalUser } from 'helper/hooks';
import { getInitialValues as getPaymentSourceInitialValues } from 'views/pages/ClientDetail/PaymentSourceTab/utils';
import toast from 'helper/toast';
import { getTabs } from './uiUtils';
import getPermission from './permissions';
import NewOrderModal from './components/NewOrderModal';
import InviteUserModal from './UserManagementTab/components/InviteUserModal';
import { TAB_KEYS, TAB_KEYS_MAPPING } from './constants';
import { useActions, useMainContentData } from './selectorData';
import { getActiveTabName } from './utils';
import classes from './ClientDetail.module.scss';

let userFilters = {};

const initBeforeUnLoad = showExitPrompt => {
  window.onbeforeunload = event => {
    if (showExitPrompt) {
      const e = event || window.event;
      e.preventDefault();
      e.stopPropagation();
      return '';
    }
  };
};
let paymentSourceForm = null;

const MainContent = ({ confirmModal, clientId, tabNames, location }) => {
  const { isInternalUser } = useIsInternalUser();
  const preselectTab = getUrlParamValueByKey(location.search, 'tab');
  const preStartOrderId = getUrlParamValueByKey(
    location.search,
    'startOrderId'
  );
  const [isEditing, setIsEditing] = useState(false);
  const [newOrderModal, setNewOrderModal] = useState(false);
  const [isEditOrder, setIsEditOrder] = useState(false);
  const [preCartOrders, setPreCartOrders] = useState([]);
  const [preCartId, setPreCartId] = useState(null);
  const [prePackage, setPrePackage] = useState(null);
  const [prePackageOrderIndex, setPrePackageOrderIndex] = useState(0);
  const [
    scheduledOrdersRefreshTrigger,
    setScheduledOrdersRefreshTrigger,
  ] = useState(0);
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const {
    getClientOrderDetail,
    getClientOrderFieldValues,
    getCompanyUsers,
    getOrderList,
  } = useActions();
  const {
    clientOrderDetail,
    orderDetailFieldValues,
    currentUser,
  } = useMainContentData();
  const permission = getPermission(currentUser.role, currentUser);
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(1);
  const [search, setSearch] = useState('');
  const orderDetailData = {
    ...(isArray(clientOrderDetail)
      ? clientOrderDetail[0] || {}
      : clientOrderDetail),
  };
  const openWarningModal = cb => {
    confirmModal(
      'Do you want to leave without saving?',
      <div>Any unsaved changes will be discarded.</div>,
      () => {
        if (cb) cb();
      },
      {
        type: 'confirm',
        onCancel: () => {},
        okText: 'Leave Without Saving',
        cancelText: 'Continue Editing',
        centered: true,
        className: classes.warningModal,
        revertButton: true,
      }
    );
  };
  const handleEditDraftOrder = record => {
    getClientOrderDetail(clientId, record.id, (res, err) => {
      if (!res) {
        toast.error(getErrorMessageFromObj(err)[0]);
        return;
      }
      const orderData = isArray(res.data) ? res.data[0] || {} : res.data || {};
      const cartItem = orderData.cart_item;
      if (!cartItem || !cartItem.cart_id) {
        // Simple draft order — no cart siblings
        setIsEditOrder(true);
        setNewOrderModal(true);
        getClientOrderFieldValues(clientId, record.id);
        return;
      }
      // Cart draft — fetch siblings: order type detail + full order detail (for requested_by) in parallel
      apiGetCartDetails(clientId, cartItem.cart_id)
        .then(cartRes => {
          const cartData = cartRes && cartRes.data ? cartRes.data : {};

          if (cartData.package_code) {
            // ── Package flow: fetch packages to get full order_type objects with IDs ──
            return apiGetPackages().then(pkgRes => {
              const allPackages = (pkgRes && pkgRes.data) || [];
              const matchedPkg = allPackages.find(
                p => p.code === cartData.package_code
              );
              if (!matchedPkg) {
                toast.error('Package configuration not found.');
                return;
              }
              const editingOrderTypeId =
                orderData.order_type_id ||
                (cartData.items || []).find(i => i.order_id === record.id)
                  ?.order?.order_type_id;
              const pkgOrderIndex = matchedPkg.order_types.findIndex(
                ot => ot.id === editingOrderTypeId
              );
              const otherCartItems = (cartData.items || []).filter(
                item => item.order_id !== record.id
              );
              const editingCartItem = (cartData.items || []).find(
                item => item.order_id === record.id
              );
              // Fetch full order details for other items to populate requested_by
              return Promise.all(
                otherCartItems.map(item =>
                  apiGetClientOrderDetail(clientId, item.order_id)
                )
              ).then(orderDetailResults => {
                const orderedSnapshots = [];
                otherCartItems.forEach((item, i) => {
                  const position = matchedPkg.order_types.findIndex(
                    ot => ot.id === item.order.order_type_id
                  );
                  if (position < 0) return;
                  const orderType = matchedPkg.order_types[position];
                  const rawOrder =
                    orderDetailResults[i] && orderDetailResults[i].data
                      ? isArray(orderDetailResults[i].data)
                        ? orderDetailResults[i].data[0] || {}
                        : orderDetailResults[i].data
                      : {};
                  orderedSnapshots[position] = {
                    id: item.order_id,
                    orderTypeName: orderType.name || '',
                    orderTypeDetail: { ...orderType },
                    formValues: {
                      order_type_id: orderType.id,
                      requested_by: rawOrder.requested_by || null,
                      price: item.order.price || orderType.price || 0,
                      rush_order: item.order.is_rush_order ? ['rush'] : [],
                      notes: item.order.notes || '',
                    },
                    addItems: {},
                    draftOrderId: item.order_id,
                    cartItemId: item.id,
                  };
                });
                // Placeholder for the editing order so the sidebar shows it
                // as a completed slot and its draftOrderId is available for update.
                if (pkgOrderIndex >= 0 && editingCartItem) {
                  const editingOrderType =
                    matchedPkg.order_types[pkgOrderIndex];
                  orderedSnapshots[pkgOrderIndex] = {
                    id: record.id,
                    orderTypeName: editingOrderType.name || '',
                    orderTypeDetail: { ...editingOrderType },
                    formValues: {
                      order_type_id: editingOrderType.id,
                      requested_by: null, // prefillFormEditMode populates this
                      price:
                        editingCartItem.order.price ||
                        editingOrderType.price ||
                        0,
                      rush_order: editingCartItem.order.is_rush_order
                        ? ['rush']
                        : [],
                      notes: editingCartItem.order.notes || '',
                    },
                    addItems: {},
                    draftOrderId: record.id,
                    cartItemId: editingCartItem.id,
                  };
                }
                unstable_batchedUpdates(() => {
                  setPreCartOrders(orderedSnapshots);
                  setPreCartId(cartData.id);
                  setPrePackage(matchedPkg);
                  setPrePackageOrderIndex(
                    pkgOrderIndex >= 0 ? pkgOrderIndex : 0
                  );
                  setIsEditOrder(true);
                  setNewOrderModal(true);
                });
                getClientOrderFieldValues(clientId, record.id);
              });
            });
          }

          // ── Simple cart flow (existing logic) ──
          const siblingItems = (cartData.items || []).filter(
            item => item.order_id !== record.id
          );
          // Per sibling, fetch order type detail AND the full order detail in parallel
          return Promise.all(
            siblingItems.map(item =>
              Promise.all([
                apiGetOrderTypeDetail(item.order.order_type_id),
                apiGetClientOrderDetail(clientId, item.order_id),
              ])
            )
          ).then(siblingResults => {
            const snapshots = siblingResults.map(
              ([typeDetailRes, orderDetailRes], idx) => {
                const item = siblingItems[idx];
                const typeDetail = (typeDetailRes && typeDetailRes.data) || {};
                const rawOrder =
                  orderDetailRes && orderDetailRes.data
                    ? isArray(orderDetailRes.data)
                      ? orderDetailRes.data[0] || {}
                      : orderDetailRes.data
                    : {};
                return {
                  id: item.order_id,
                  orderTypeName: typeDetail.name || '',
                  orderTypeDetail: { ...typeDetail },
                  formValues: {
                    order_type_id: item.order.order_type_id,
                    requested_by: rawOrder.requested_by || null,
                    price: item.order.price || typeDetail.price || 0,
                    rush_order: item.order.is_rush_order ? ['rush'] : [],
                    notes: item.order.notes || '',
                  },
                  addItems: {},
                  draftOrderId: item.order_id,
                  cartItemId: item.id,
                };
              }
            );
            unstable_batchedUpdates(() => {
              setPreCartOrders(snapshots);
              setPreCartId(cartItem.cart_id);
              setIsEditOrder(true);
              setNewOrderModal(true);
            });
            getClientOrderFieldValues(clientId, record.id);
          });
        })
        .catch(() => {
          toast.error('Failed to load cart details.');
        });
    });
  };
  const handleSubmitOrderCb = ({ cards, accounts }) => {
    if (paymentSourceForm) {
      const paymentSourceValues = getPaymentSourceInitialValues({
        accounts,
        cards,
      });
      paymentSourceForm.setFieldsValue({
        ...paymentSourceValues,
      });
    }
  };
  const tabs = getTabs({
    search,
    setSearch,
    confirmModal,
    setIsEditing,
    openWarningModal,
    activeTab,
    clientId,
    onEditDraftOrder: handleEditDraftOrder,
    scheduledOrdersRefreshTrigger,
    permission,
    tabNames,
    paymentSourceFormChange: psForm => {
      paymentSourceForm = psForm;
    },
    onUsersFilterChanged: obj => {
      userFilters = obj;
    },
  });
  const activeTabName = getActiveTabName(activeTab, tabs);

  useEffect(() => {
    initBeforeUnLoad(isEditing);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    if (preselectTab || preStartOrderId) {
      const preselectTabKey = TAB_KEYS_MAPPING[preselectTab] || '';
      const preselectTabIndex = findIndex(tabs, { key: preselectTabKey });
      if (preselectTabIndex >= 0) {
        setActiveTab(preselectTabIndex + 1);
      }
      if (!preStartOrderId) {
        removeAllSearchParam(history);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectTab, preStartOrderId]);

  useEffect(() => {
    userFilters = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <PageContent initFullHeight hasMargin className={classes.mainContent}>
      <RouterLeavingGuard
        when={isEditing}
        navigate={path => {
          history.push(path);
        }}
        handleOpenModal={cb => {
          openWarningModal(cb);
        }}
      />
      <Tabs
        tabs={tabs}
        type="line"
        activeKey={activeTab.toString()}
        onChange={activeKey => {
          const iActiveKey = parseInt(activeKey, 10);
          if (iActiveKey !== 1 && isEditing) {
            openWarningModal(() => {
              setActiveTab(iActiveKey);
              setIsEditing(false);
            });
          } else {
            setActiveTab(iActiveKey);
          }
        }}
        className={classes.tabs}
      />
      {isInternalUser &&
      [TAB_KEYS.CLIENT_RECORD, TAB_KEYS.USER_MANAGEMENT].includes(
        activeTabName
      ) ? (
        <div className={classes.tabHeaderButton}>
          <Button
            type="primary"
            size="medium"
            onClick={() => {
              setInviteUserModal(true);
            }}
            color={ButtonColors.BLUE}
            leftIcon={<PlusCircleOutlined />}
          >
            Invite User
          </Button>
        </div>
      ) : (
        <>
          {!!permission.newOrder && (
            <div className={classes.tabHeaderButton}>
              <Button
                type="primary"
                size="medium"
                onClick={() => {
                  setIsEditOrder(false);
                  setNewOrderModal(true);
                }}
                color={ButtonColors.BLUE}
                leftIcon={<PlusCircleOutlined />}
              >
                New Order
              </Button>
            </div>
          )}
        </>
      )}
      <NewOrderModal
        clientId={clientId}
        setActiveTab={setActiveTab}
        isOpen={newOrderModal}
        handleClose={() => {
          setNewOrderModal(false);
          setIsEditOrder(false);
          setPreCartOrders([]);
          setPreCartId(null);
          setPrePackage(null);
          setPrePackageOrderIndex(0);
          getOrderList(clientId);
          setScheduledOrdersRefreshTrigger(prev => prev + 1);
        }}
        orderDetailData={orderDetailData}
        isEditOrder={isEditOrder}
        orderDetailFieldValues={isEditOrder ? orderDetailFieldValues : []}
        initialCartOrders={isEditOrder ? preCartOrders : []}
        initialCartId={isEditOrder ? preCartId : null}
        initialSelectedPackage={isEditOrder ? prePackage : null}
        initialPackageOrderIndex={isEditOrder ? prePackageOrderIndex : 0}
        onSubmitOrderCb={handleSubmitOrderCb}
        permission={permission}
      />
      <InviteUserModal
        isOpen={inviteUserModal}
        handleClose={() => {
          setInviteUserModal(false);
        }}
        clientId={clientId}
        onSubmitCb={values => {
          getCompanyUsers(clientId, {
            ...userFilters,
          });
          setInviteUserModal(false);
          confirmModal(
            'Invitation has been sent to the user',
            <div>
              <span>The invitation email has been sent to</span>
              <br />
              <span>{values.email}.</span>
            </div>,
            () => {}
          );
        }}
      />
    </PageContent>
  );
};

export default MainContent;
